import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import RailHeader from '../home/RailHeader';

/**
 * RisingCampaignsRail — horizontal scrolling rail of campaigns
 * with positive votes_last_48h velocity, ordered by that metric.
 *
 * Props:
 *   summary   — from useSlicePot().summary
 *   campaigns — active campaigns from contentService.getCrowdfundingProjects
 */
const RisingCampaignsRail = ({ summary, campaigns }) => {
  const leaderboard = summary?.leaderboard ?? [];

  /* Only entries with recent activity */
  const risingEntries = leaderboard
    .filter((e) => (e.votes_last_48h ?? 0) > 0)
    .sort((a, b) => b.votes_last_48h - a.votes_last_48h)
    .slice(0, 8);

  if (risingEntries.length === 0) return null;

  /* Map leaderboard entries to full campaign objects */
  const risingCampaigns = risingEntries
    .map((entry) => {
      const campaign = campaigns.find((c) => c.id === entry.campaign_id);
      return campaign
        ? { ...campaign, votes_last_48h: entry.votes_last_48h, totalVotes: entry.votes }
        : null;
    })
    .filter(Boolean);

  if (risingCampaigns.length === 0) return null;

  return (
    <section>
      <RailHeader title="Rising Fast" />
      <p className="text-sm text-muted-foreground -mt-1 mb-4">
        Campaigns gaining community traction in the last 48 hours.
      </p>

      {/* Horizontal scrolling rail */}
      <div className="flex gap-5 overflow-x-auto pb-4 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 snap-x snap-mandatory scrollbar-hide">
        {risingCampaigns.map((campaign, i) => (
          <RisingCard key={campaign.id} campaign={campaign} index={i} />
        ))}
      </div>
    </section>
  );
};

/* ─── Individual rising card ──────────────────────────────────── */
const RisingCard = ({ campaign, index }) => {
  const pct = Math.min(campaign.progressPercentage ?? 0, 100);

  return (
    <Link
      to={`/crowdfund/${campaign.id}`}
      className="group shrink-0 w-52 sm:w-60 snap-start block border border-border/40 rounded-lg overflow-hidden hover:border-amber-500/30 transition-colors duration-200 bg-card/20"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Campaign image */}
      {campaign.image && (
        <div className="aspect-[4/3] bg-muted overflow-hidden">
          <img
            src={campaign.image}
            alt={campaign.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}

      <div className="p-3 space-y-2">
        {/* Velocity badge */}
        <div className="flex items-center gap-1.5">
          <TrendingUp className="h-3 w-3 text-amber-400 shrink-0" />
          <span className="text-[10px] uppercase tracking-widest text-amber-400 font-medium">
            +{campaign.votes_last_48h.toLocaleString()} last 48h
          </span>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-sm leading-snug text-foreground line-clamp-2">
          {campaign.title}
        </h3>

        {/* Creator */}
        {campaign.creator?.name && (
          <p className="text-xs text-muted-foreground truncate">
            {campaign.creator.name}
          </p>
        )}

        {/* Mini progress */}
        <div className="space-y-1 pt-1">
          <div className="relative h-[2px] bg-border/60 w-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-primary transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground tabular-nums">
            <span>{pct}% funded</span>
            {campaign.totalVotes > 0 && (
              <span>🍕 {campaign.totalVotes.toLocaleString()} votes</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default RisingCampaignsRail;
